/**
 * Normalized snapshots for provider note auto-save diffing.
 * Kept separate from UI so versioning/undo can reuse the same shape later.
 */
import type { QuestionnaireResponseItem } from '@medplum/fhirtypes';
import isEqual from 'fast-deep-equal';
import type { NoteMetadataSnapshot } from '@/schemas/note-metadata.schema';
import type { DocumentComment } from '../../supervision/types';
import type { ParticipantAttendanceFhirStatus } from './participantAttendance';
import { stripParticipantAttendanceGroup } from './participantAttendance';
import { stripQuestionnaireResponseItemIds } from './questionnaireResponseShape';
import { omit } from '@/lib/omit';

export interface NoteAutosaveSnapshot {
  templateId: string | null;
  responseItems: QuestionnaireResponseItem[] | undefined;
  metadata: NoteMetadataSnapshot | null;
  attendanceByPatient: Record<string, ParticipantAttendanceFhirStatus | undefined>;
  locationByPatient: Record<string, string | undefined>;
  comments: DocumentComment[];
}

/** Fields excluded from diff — volatile, server-generated, or check output. */
function normalizeMetadata(metadata: NoteMetadataSnapshot | null): NoteMetadataSnapshot | null {
  if (!metadata) return null;
  return omit(metadata, 'encounterScrubberReview');
}

function sortRecordKeys<T>(record: Record<string, T | undefined>): Record<string, T | undefined> {
  return Object.fromEntries(
    Object.keys(record)
      .sort()
      .map((key) => [key, record[key]]),
  );
}

function normalizeComments(comments: DocumentComment[]): DocumentComment[] {
  return [...comments]
    .map(({ id, anchor, text, createdAt, authorId, authorName, authorRole, resolvedAt }) => ({
      id,
      anchor,
      text,
      createdAt,
      authorId,
      authorName,
      authorRole,
      resolvedAt,
    }))
    .sort((a, b) => a.id.localeCompare(b.id));
}

/** Stable normalized form used for deep equality. */
export function normalizeNoteForAutosaveDiff(snapshot: NoteAutosaveSnapshot): NoteAutosaveSnapshot {
  return {
    templateId: snapshot.templateId,
    // Item ids are server-assigned and can change on every save — diffing on
    // them causes endless autosave loops, so compare structure/answers only.
    responseItems: stripQuestionnaireResponseItemIds(
      stripParticipantAttendanceGroup(snapshot.responseItems),
    ),
    metadata: normalizeMetadata(snapshot.metadata),
    attendanceByPatient: sortRecordKeys(snapshot.attendanceByPatient),
    locationByPatient: sortRecordKeys(snapshot.locationByPatient),
    comments: normalizeComments(snapshot.comments),
  };
}

export function createAutosaveSnapshot(snapshot: NoteAutosaveSnapshot): NoteAutosaveSnapshot {
  return normalizeNoteForAutosaveDiff(snapshot);
}

export function hasMeaningfulAutosaveChanges(
  current: NoteAutosaveSnapshot,
  lastSaved: NoteAutosaveSnapshot,
): boolean {
  return !isEqual(
    normalizeNoteForAutosaveDiff(current),
    normalizeNoteForAutosaveDiff(lastSaved),
  );
}
